/**
 * The Edit-with-AI panel — the reference `SkillChatPanel`, rendered in this
 * app's own chat idiom: the same `Markdown`, `AgentSteps`, `InterruptPanel`,
 * `ActivePlanPanel`, `ContextMeter` and the mode/model chips as the Threads
 * pane, so the two chat surfaces cannot drift apart.
 *
 * Header: the title, the thread History menu, New thread, View code. Body: the
 * transcript with the interrupt card *in* it (where the turn stopped to ask).
 * Foot: the composer — attach, meter, mode, model, send/stop — disabled with
 * the reason whenever the skill's matching instance is not the selected one,
 * because editing tests against that document or not at all (design §9 ph. 2).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { Attachment } from "../../lib/attachments";
import { ACCEPT_ATTR } from "../../lib/attachments";
import { resolveContextUsage } from "../../lib/chat";
import type { CurrentModel, ModelOption, AgentMode, ReasoningEffort } from "../../lib/models";
import type { RegenThreadSummary } from "../../lib/skills";
import { canSendInThread, threadStatusKey } from "../../lib/threadStatus";
import { useAttachments } from "../../hooks/useAttachments";
import type { SkillEditView } from "../../hooks/useSkillEdit";
import { ActivePlanPanel } from "../chat/ActivePlanPanel";
import { AgentSteps } from "../chat/AgentSteps";
import { MessageAttachments } from "../chat/MessageAttachments";
import { CopyButton } from "../chat/CopyButton";
import { AgentModeSelector, ModelSelector } from "../chat/ComposerControls";
import { ContextMeter } from "../chat/ContextMeter";
import { InterruptPanel } from "../chat/InterruptPanel";
import { Markdown } from "../chat/Markdown";
import { ReasoningSection } from "../chat/ReasoningSection";
import { Spinner } from "../settings/Spinner";
import { CodeIcon, HistoryIcon, LoaderIcon, PlusIcon } from "./icons";
import { ParameterChanges } from "./SkillPatchCard";

export function SkillEditorChat({
  view,
  token,
  blocked,
  models,
  currentModel,
  onSelectModel,
  contextWindow,
  agentMode,
  onAgentMode,
  reasoningEffort,
  onReasoningEffort,
  showingCode,
  onToggleCode,
  parameterSchema,
}: {
  view: SkillEditView;
  token: string | null;
  /** Why the composer is disabled, or null when it is not. */
  blocked: string | null;
  models: ModelOption[];
  currentModel: CurrentModel | null;
  onSelectModel: (model: CurrentModel) => void;
  contextWindow: number | null;
  agentMode: AgentMode;
  onAgentMode: (mode: AgentMode) => void;
  /** The reasoning chip's value (#111), or null to hide the chip for a model that does not reason. */
  reasoningEffort: ReasoningEffort | null;
  onReasoningEffort: (effort: ReasoningEffort) => void;
  showingCode: boolean;
  onToggleCode: () => void;
  parameterSchema: unknown;
}) {
  const { t, locale } = useLocale();
  const [draft, setDraft] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const attachments = useAttachments(token);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const resize = () => {
      input.style.height = "auto";
      input.style.height = `${input.scrollHeight}px`;
    };
    resize();
    let width = input.clientWidth;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width !== width) {
        width = entry.contentRect.width;
        resize();
      }
    });
    observer.observe(input);
    return () => observer.disconnect();
  }, [draft]);

  // Follow the newest message — the reference's scroll anchor.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [view.messages, view.interrupt, view.busy]);

  useEffect(() => {
    if (!historyOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setHistoryOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHistoryOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [historyOpen]);

  const locked = view.busy || view.interrupt !== null;
  /**
   * A conversation the gate refused (`lib/threadStatus.ts`) blocks the composer with its
   * own sentence: sending into it can only be refused again. The skill-level reasons
   * (permission, instance, a run) still win, being the steadier facts.
   */
  const statusKey = threadStatusKey(view.threadStatus);
  const composerBlocked = blocked ?? (canSendInThread(view.threadStatus) || !statusKey ? null : t(statusKey));
  const canSend =
    composerBlocked === null &&
    !locked &&
    (draft.trim().length > 0 || attachments.sendable().length > 0);

  const submit = () => {
    if (!canSend) return;
    const ready: Attachment[] = attachments.sendable();
    view.send(draft, ready.length > 0 ? ready : undefined);
    setDraft("");
    attachments.clear();
  };

  const lastId = view.messages[view.messages.length - 1]?.id;

  return (
    <div className="skill-editor-chat">
      <header className="skill-chat-head">
        <h3>
          {t("skillEdit.chatTitle")}
          <span className="skill-chat-beta">Beta</span>
        </h3>
        <div className="skill-chat-actions">
          {view.threads.length > 0 ? (
            <div className="skill-menu" ref={menuRef}>
              <button
                type="button"
                className="settings-btn secondary is-small"
                disabled={locked}
                aria-haspopup="menu"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((v) => !v)}
              >
                <HistoryIcon size={13} />
                {t("common.history")}
              </button>
              {historyOpen ? (
                <div className="skill-menu-pop skill-thread-menu" role="menu">
                  {view.threads.map((thread: RegenThreadSummary) => (
                    <button
                      key={thread.id}
                      type="button"
                      role="menuitem"
                      className={`skill-menu-item${thread.id === view.currentThreadId ? " is-active" : ""}`}
                      onClick={() => {
                        setHistoryOpen(false);
                        void view.switchThread(thread.id);
                      }}
                    >
                      <span className="skill-thread-title">{thread.title || t("common.newThread")}</span>
                      <span className="skill-thread-date">
                        {formatThreadDate(thread.last_used_at ?? thread.created_at, locale)}
                        {/* An AutoCAD conversation names the year it last ran against: the
                            History menu spans every year folder of the skill. */}
                        {thread.version_folder ? ` · AutoCAD ${thread.version_folder}` : ""}
                        {thread.id === view.currentThreadId ? ` · ${t("skillEdit.currentThread")}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            className="settings-btn secondary is-small"
            disabled={locked}
            onClick={view.resetThread}
          >
            <PlusIcon size={13} />
            {t("common.newThread")}
          </button>
          <button type="button" className="settings-btn secondary is-small" onClick={onToggleCode}>
            <CodeIcon size={13} />
            {showingCode ? t("skillEdit.backToChat") : t("skillEdit.viewCode")}
          </button>
        </div>
      </header>

      <div className="skill-chat-scroll" ref={scrollRef}>
        <div className="chat-thread">
          {statusKey ? (
            <div className="skill-thread-status" role="status" data-status={view.threadStatus.kind}>
              <p className="skill-thread-status-text">{t(statusKey)}</p>
              {view.threadStatus.kind === "load_failed" && view.threadStatus.message ? (
                <p className="skill-thread-status-detail muted">{view.threadStatus.message}</p>
              ) : null}
              <div className="skill-thread-status-actions">
                {view.threadStatus.kind === "load_failed" ? (
                  <button type="button" className="settings-btn secondary is-small" disabled={locked} onClick={view.retryThread}>
                    {t("plugins.retry")}
                  </button>
                ) : null}
                <button type="button" className="settings-btn is-small" disabled={locked} onClick={view.resetThread}>
                  <PlusIcon size={13} />
                  {t("threadStatus.startNew")}
                </button>
              </div>
            </div>
          ) : null}
          {view.loadingHistory && view.messages.length === 0 ? (
            <div className="skills-center muted">
              <LoaderIcon size={14} /> {t("common.historyLoading")}
            </div>
          ) : view.messages.length === 0 && !view.busy && !statusKey ? (
            <div className="skill-chat-empty">
              <p className="skill-chat-empty-title">{t("skillEdit.chatTitle")}</p>
              <p className="skill-chat-empty-body">{t("skillEdit.emptyState")}</p>
            </div>
          ) : (
            view.messages.map((message) => {
              const streamingRow = view.streaming && message.id === lastId;
              /**
               * Copy, on the same two rules the Threads pane uses (owner) — the same component,
               * so the two transcripts cannot drift apart.
               *
               * A system notice is neither the user's words nor the model's, so it gets no
               * control, and there is nothing to copy from an empty row.
               */
              const copyable = message.role !== "system" && message.text.length > 0;
              return (
                <div key={message.id} className={`chat-msg ${message.role}`}>
                  {/* The user's sits outside the bubble, to its left, and stays put through the
                      reply that follows: their message is finished the moment it is sent. */}
                  {copyable && message.role === "user" ? (
                    <div className="msg-actions">
                      <CopyButton text={message.text} />
                    </div>
                  ) : null}
                  <div className="chat-msg-body">
                    {message.reasoning ? (
                      <ReasoningSection text={message.reasoning} complete={!streamingRow} />
                    ) : null}
                    {message.steps?.length ? <AgentSteps steps={message.steps} /> : null}
                    {message.role === "user" ? <MessageAttachments attachments={message.attachments} /> : null}
                    {message.text ? (
                      message.role === "user" ? (
                        <div className="chat-msg-text">{message.text}</div>
                      ) : (
                        <Markdown text={message.text} />
                      )
                    ) : null}
                    {/* The assistant's sits below its reply, and not while that reply is still
                        being written — it would keep moving out from under the cursor. */}
                    {copyable && message.role === "assistant" && !streamingRow ? (
                      <div className="msg-actions">
                        <CopyButton text={message.text} />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
          {/* The Threads pane's bubble of dots (item BS), for as long as the turn is running and
              not waiting on the user. It used to show only for a reply with nothing in it yet,
              so once a tool step had finished the panel sat still until the next words came. */}
          {view.busy && !view.interrupt ? (
            <div className="chat-msg assistant chat-thinking-row">
              <div className="chat-thinking" role="status" aria-label={t("chat.thinking")}>
                <span className="chat-typing">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            </div>
          ) : null}
          {view.interrupt?.action_requests.map((action, index) => {
            const artifact = action.args?.["artifact"];
            return action.name === "commit_skill_version" ? (
              <ParameterChanges
                key={index}
                baseline={parameterSchema}
                proposed={artifact && typeof artifact === "object" ? (artifact as Record<string, unknown>)["parameterSchema"] : undefined}
              />
            ) : null;
          })}
          {view.interrupt ? (
            <InterruptPanel interrupt={view.interrupt} busy={view.streaming} onRespond={view.respond} />
          ) : null}
          {/* The approved plan's floating button — last in the scrolling transcript so it
              sticks to the bottom edge, as in the Threads pane. */}
          {view.activePlan ? <ActivePlanPanel plan={view.activePlan} onDismiss={view.clearPlan} /> : null}
        </div>
      </div>

      <div className="skill-chat-foot">
        {view.error ? (
          <p className="composer-error" role="alert">
            {view.error}
          </p>
        ) : null}
        <div className="composer">
          {attachments.staged.length > 0 ? (
            <ul className="attach-list">
              {attachments.staged.map((a) => (
                <li key={a.id} className={`attach-chip is-${a.status}`}>
                  {a.kind === "image" ? (
                    <img className="attach-thumb" src={a.url} alt="" aria-hidden="true" />
                  ) : (
                    <span className="attach-kind">{a.kind === "pdf" ? "PDF" : "XLS"}</span>
                  )}
                  <span className="attach-name">{a.filename}</span>
                  {a.status === "uploading" ? <Spinner size={12} /> : null}
                  {a.status === "failed" ? <span className="attach-failed">{t("common.failed")}</span> : null}
                  <button
                    type="button"
                    className="attach-remove"
                    aria-label={t("messageInput.removeAttachment")}
                    onClick={() => attachments.remove(a.id)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {attachments.error ? (
            <p className="attach-error" role="alert">
              {attachments.error}
            </p>
          ) : null}
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            disabled={composerBlocked !== null || locked}
            placeholder={
              composerBlocked ??
              (view.interrupt ? t("skillEdit.awaitingApprovalPlaceholder") : t("skillEdit.inputPlaceholder"))
            }
            onChange={(e) => setDraft(e.target.value)}
            onPaste={(e) => {
              // A pasted screenshot is an attachment, as with the + button; text pastes as text.
              const files = e.clipboardData.files;
              if (files.length === 0) return;
              e.preventDefault();
              if (composerBlocked === null && !locked) attachments.add(Array.from(files));
            }}
            onKeyDown={(e) => {
              // The Enter that confirms an input-method candidate (注音, 倉頡…) is the
              // IME's, not a send: sending then cleared the box mid-composition and the
              // IME wrote the words straight back, so the message went out and stayed.
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="composer-actions">
            <div className="composer-controls">
              <input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files) attachments.add(e.target.files);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="composer-attach"
                disabled={composerBlocked !== null || locked}
                title={t("messageInput.attach")}
                aria-label={t("messageInput.attach")}
                onClick={() => fileRef.current?.click()}
              >
                +
              </button>
            </div>
            <div className="composer-controls is-right">
              <ContextMeter usage={resolveContextUsage(view.usage, contextWindow)} />
              <AgentModeSelector value={agentMode} onChange={onAgentMode} disabled={locked} />
              <ModelSelector
                models={models}
                current={currentModel}
                onSelect={onSelectModel}
                reasoningEffort={reasoningEffort}
                onReasoningEffort={onReasoningEffort}
                disabled={locked}
              />
              {view.streaming ? (
                <button
                  type="button"
                  className="composer-send is-stop"
                  aria-label={t("chat.stop")}
                  title={t("chat.stop")}
                  onClick={view.stop}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect width="18" height="18" x="3" y="3" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  className="composer-send"
                  aria-label={t("chat.send")}
                  title={t("chat.send")}
                  disabled={!canSend}
                  onClick={submit}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m5 12 7-7 7 7" />
                    <path d="M12 19V5" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        {composerBlocked ? <p className="composer-hint">{composerBlocked}</p> : null}
      </div>
    </div>
  );
}

function formatThreadDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale === "zh" ? "zh-TW" : undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
